import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    Settings,
    ChevronDown,
    Wrench,
    DollarSign,
    LogOut,
    User
} from 'lucide-react';
import { getConfig } from '../../config';
import { useAuth } from '../../contexts/AuthContext'; // Import useAuth hook

const Navbar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [configInfo, setConfigInfo] = useState(null);
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);

    const { currentUser, logout } = useAuth();

    useEffect(() => {
        const loadConfig = async () => {
            const config = await getConfig();
            setConfigInfo(config);
        };

        loadConfig();
    }, []);

    const isActive = (path) => {
        return location.pathname === path ? 'bg-gray-900' : 'hover:bg-gray-700';
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setIsUserMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        logout();
    };

    return (
        <nav className="bg-gray-800 text-white">
            <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between h-16">
                    <div className="flex items-center">
                        <Link to="/" className="flex items-center space-x-2">
                            <Wrench className="h-6 w-6 text-blue-400" />
                            <span className="text-xl font-bold">AWS Utilities</span>
                        </Link>

                        <div className="hidden md:block ml-10">
                            <div className="flex items-center space-x-4">
                                <Link
                                    to="/"
                                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium ${isActive('/')}`}
                                >
                                    <LayoutDashboard className="h-4 w-4" />
                                    <span>Dashboard</span>
                                </Link>

                                {/* Tools Dropdown */}
                                <div
                                    ref={dropdownRef}
                                    className="relative"
                                    onMouseEnter={() => setIsDropdownOpen(true)}
                                >
                                    <button
                                        className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-700"
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    >
                                        <Wrench className="h-4 w-4" />
                                        <span>Tools</span>
                                        <ChevronDown className={`h-4 w-4 transform transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {isDropdownOpen && (
                                        <div
                                            className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-gray-700 ring-1 ring-black ring-opacity-5 z-50"
                                            onMouseLeave={() => setIsDropdownOpen(false)}
                                        >
                                            <div className="py-1" role="menu">
                                                <Link
                                                    to="/moneysaver"
                                                    className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-100 hover:bg-gray-600"
                                                    role="menuitem"
                                                    onClick={() => setIsDropdownOpen(false)}
                                                >
                                                    <DollarSign className="h-4 w-4" />
                                                    <span>Moneysaver</span>
                                                </Link>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <Link
                                    to="/settings"
                                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium ${isActive('/settings')}`}
                                >
                                    <Settings className="h-4 w-4" />
                                    <span>Settings</span>
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">

                        {/* User menu */}
                        <div ref={userMenuRef} className="relative">
                            <button
                                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                            >
                                <span className="sr-only">Open user menu</span>
                                <div className="h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center">
                                    <User className="h-5 w-5 text-gray-300" />
                                </div>
                            </button>

                            {isUserMenuOpen && (
                                <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-50">
                                    <div className="px-4 py-2 text-xs text-gray-500">
                                        Signed in as <span className="font-medium text-gray-900">
                                            {currentUser?.email || currentUser?.username || 'User'}
                                        </span>
                                    </div>

                                    <div className="border-t border-gray-200"></div>

                                    <Link
                                        to="/profile"
                                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        onClick={() => setIsUserMenuOpen(false)}
                                    >
                                        Your Profile
                                    </Link>

                                    <button
                                        onClick={handleLogout}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        Sign out
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;